import { createContext, useContext, useEffect, useRef } from 'react';
import type { User, WireLine } from '@/api/client';

// What every page shares with the shell around it: the environment on screen, privacy mode, the toast, the palette.
export interface Command {
    icon: string;
    label: string;
    key?: string;
    run: () => void;
}

export interface Shell {
    user: User;
    env: string;
    envs: readonly string[];
    setEnv: (env: string) => void;
    privacy: boolean;
    setPrivacy: (privacy: boolean) => void;
    say: (message: string) => void;
    // Opens a line in EXPLORE, from anywhere (the palette, an issue, an alert).
    openLine: (line: Pick<WireLine, 'env' | 'id'>) => void;
    // A page's own commands for the palette: a getter, read when the palette opens.
    setPageCommands: (commands: () => Command[]) => void;
}

export const ShellContext = createContext<Shell | null>(null);

export function useShell(): Shell {
    const shell = useContext(ShellContext);
    if (!shell) {
        throw new Error('useShell outside the shell');
    }

    return shell;
}

// The page's commands, current at every render; the palette asks for them when it opens.
export function usePageCommands(commands: Command[]): void {
    const { setPageCommands } = useShell();
    const latest = useRef(commands);
    useEffect(() => {
        latest.current = commands;
    });
    useEffect(() => {
        setPageCommands(() => latest.current);

        return () => setPageCommands(() => []);
    }, [setPageCommands]);
}

// G, then a module's letter, is the shell's: a page leaves alone the letter that follows a G. Whichever listener runs
// first, the page sees the G pending, or the shell's preventDefault on the letter.
let goAt = 0;
export const markGo = (): void => {
    goAt = Date.now();
};
export const clearGo = (): void => {
    goAt = 0;
};
export const goPending = (): boolean => Date.now() - goAt < 1200;
export const shellKey = (event: KeyboardEvent): boolean => goPending() || event.defaultPrevented;
